import PropTypes from "prop-types";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { Minus, Plus, Sparkles, WandSparkles } from "lucide-react";

import { Button } from "../../../components/Button";
import { InfoTooltip } from "../../../components/InfoTooltip";
import { CustomSelector } from "../../../components/CustomSelector";
import { CustomTextArea } from "../../../components/CustomTextArea";
import { CustomTextField } from "../../../components/CustomTextField";
import { AgencyPromptsButton } from "../../../components/prompts/AgencyPromptsButton";
import { brand } from "../../../theme/theme";
import {
  DURATION_RANGE,
  FREQUENCY_RANGE,
  LANGUAGE_OPTIONS,
  LANGUAGE_TYPE_OPTIONS,
  TONE_OPTIONS,
  panelSx,
  sectionLabelSx,
} from "./campaignUi";

function SelectField({ label, options, value, onChange, disabled, freeSolo = false }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>{label}</Typography>
      <CustomSelector
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
        placeholder="Seleccionar"
        searchable={freeSolo}
        freeSolo={freeSolo}
      />
    </Box>
  );
}

function NumberStepper({ label, value, onChange, min, max, unit, disabled }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.5, py: 0.25, borderRadius: "8px", border: "1px solid #E5E7EB" }}>
        <IconButton size="small" aria-label={`Menos ${label}`} disabled={disabled || value <= min} onClick={() => onChange(value - 1)}>
          <Minus size={14} />
        </IconButton>
        <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 700, fontSize: "0.875rem", color: "#111827" }}>
          {value} <Box component="span" sx={{ fontWeight: 500, color: "#6B7280" }}>{unit}</Box>
        </Typography>
        <IconButton size="small" aria-label={`Más ${label}`} disabled={disabled || value >= max} onClick={() => onChange(value + 1)}>
          <Plus size={14} />
        </IconButton>
      </Box>
    </Box>
  );
}

// Adaptation prompts are shown later, next to the channel picker.
const SINGLE_PROMPT_STEPS = ["individual_content"];
const CAMPAIGN_PROMPT_STEPS = ["campaign_generation", "individual_content"];

/**
 * Left column of the batch page: the briefing. Editable before generating.
 * Channels are chosen per piece below its base text, not here.
 */
export function CampaignBriefingForm({
  clientName,
  value,
  onChange,
  fieldsEditable,
  actionLabel,
  actionDisabled,
  actionLoading,
  onAction,
  estimate,
  single = false,
  clientId,
}) {
  const update = (patch) => onChange({ ...value, ...patch });
  // A single piece is briefed by its topic (required); a batch by an optional monthly objective.
  const textField = single ? "topic" : "objective";
  const textValue = value[textField] || "";
  const initialPrompt = value.initialPrompt || "";

  return (
    <Box
      data-tutorial={single ? "cpm-input-form" : "cg-campaign-config"}
      sx={{ ...panelSx, p: 2.5, display: "flex", flexDirection: "column", gap: 2.25, position: { md: "sticky" }, top: { md: 16 } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: "10px", background: brand.gradientDiagonal, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <WandSparkles size={17} color="#fff" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.0625rem", color: "#0F172A" }}>
            {single ? "Nuevo contenido" : "Briefing de la tanda"}
          </Typography>
          {clientName && <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>{clientName}</Typography>}
        </Box>
      </Box>

      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
          <Typography component="label" htmlFor="campaign-objective" sx={{ ...sectionLabelSx, mb: 0 }}>
            {single ? "Título del contenido" : "Objetivo del mes"}
          </Typography>
          {single && (
            <InfoTooltip
              title="No es el prompt: solo el título o tema de la pieza. A partir de él la IA generará el contenido base (el prompt) en el siguiente paso."
              placement="top"
              size={14}
            />
          )}
        </Box>
        {fieldsEditable && single ? (
          <CustomTextField
            id="campaign-objective"
            value={textValue}
            onChange={(event) => update({ topic: event.target.value })}
            placeholder="Ej: Promoción de Navidad -40%"
          />
        ) : fieldsEditable ? (
          <CustomTextArea
            id="campaign-objective"
            rows={3}
            value={textValue}
            onChange={(event) => update({ objective: event.target.value })}
            placeholder="Ej: traer más solicitudes de cita de ortodoncia invisible y reforzar la autoridad en implantes."
          />
        ) : (
          <Box sx={{ p: 1.5, borderRadius: "8px", border: "1px solid #E5E7EB", fontSize: "0.875rem", color: textValue ? "#374151" : "#9CA3AF", lineHeight: 1.5, whiteSpace: "pre-line" }}>
            {textValue || "Sin objetivo concreto: la IA propone temas a partir de la ficha del cliente."}
          </Box>
        )}
      </Box>

      {single && (fieldsEditable || initialPrompt) && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <Typography component="label" htmlFor="content-initial-prompt" sx={{ ...sectionLabelSx, mb: 0 }}>
              Prompts para la IA (opcional)
            </Typography>
            <InfoTooltip
              title="Lo que quieres que tenga en cuenta la IA al redactar el contenido base: enfoque, datos, ofertas, qué evitar..."
              placement="top"
              size={14}
            />
          </Box>
          {fieldsEditable ? (
            <CustomTextArea
              id="content-initial-prompt"
              rows={3}
              value={initialPrompt}
              onChange={(event) => update({ initialPrompt: event.target.value })}
              placeholder="Ej: céntrate en el envío gratis, menciona que la oferta acaba el domingo y no hables de precios."
            />
          ) : (
            <Box sx={{ p: 1.5, borderRadius: "8px", border: "1px solid #E5E7EB", fontSize: "0.875rem", color: "#374151", lineHeight: 1.5, whiteSpace: "pre-line" }}>
              {initialPrompt}
            </Box>
          )}
        </Box>
      )}

      <Box>
        <Typography sx={sectionLabelSx}>Tono y voz de marca</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <SelectField
            label="Tono"
            options={TONE_OPTIONS}
            value={value.tone}
            onChange={(tone) => update({ tone })}
            disabled={!fieldsEditable}
            freeSolo
          />
          <SelectField
            label="Tipo de lenguaje"
            options={LANGUAGE_TYPE_OPTIONS}
            value={value.languageType}
            onChange={(languageType) => update({ languageType })}
            disabled={!fieldsEditable}
            freeSolo
          />
          <SelectField
            label="Idioma"
            options={LANGUAGE_OPTIONS}
            value={value.language}
            onChange={(language) => update({ language })}
            disabled={!fieldsEditable}
          />
        </Box>
      </Box>

      {!single && (
        <Box>
          <Typography sx={sectionLabelSx}>Ritmo</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <NumberStepper
              label="Por semana"
              unit="/sem"
              value={value.frequency}
              onChange={(frequency) => update({ frequency })}
              min={FREQUENCY_RANGE.min}
              max={FREQUENCY_RANGE.max}
              disabled={!fieldsEditable}
            />
            <NumberStepper
              label="Duración"
              unit={value.duration === 1 ? "semana" : "semanas"}
              value={value.duration}
              onChange={(duration) => update({ duration })}
              min={DURATION_RANGE.min}
              max={DURATION_RANGE.max}
              disabled={!fieldsEditable}
            />
          </Box>
        </Box>
      )}

      <AgencyPromptsButton
        steps={single ? SINGLE_PROMPT_STEPS : CAMPAIGN_PROMPT_STEPS}
        clientId={clientId}
        sx={{ alignSelf: "flex-start" }}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}>
        <Button
          onClick={onAction}
          disabled={actionDisabled}
          sx={{ height: 46, borderRadius: "999px", fontSize: "0.9375rem", background: brand.gradient, "&:hover": { background: brand.gradientHover } }}
        >
          {actionLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <Sparkles size={16} />}
          {actionLabel}
        </Button>
        {estimate && (
          <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", textAlign: "center" }}>{estimate}</Typography>
        )}
      </Box>
    </Box>
  );
}

CampaignBriefingForm.propTypes = {
  clientName: PropTypes.string,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  fieldsEditable: PropTypes.bool,
  actionLabel: PropTypes.node,
  actionDisabled: PropTypes.bool,
  actionLoading: PropTypes.bool,
  onAction: PropTypes.func,
  estimate: PropTypes.node,
  single: PropTypes.bool,
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
